export const MAX_CODE_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_CODE_CHARS = 120_000;

const SUPPORTED_EXTENSIONS = new Set([
  "js",
  "ts",
  "jsx",
  "tsx",
  "py",
  "java",
  "go",
  "json",
  "yaml",
  "yml",
  "txt",
  "md",
  "text",
  "sh",
  "bash",
  "cs",
  "rb",
  "php",
]);

export interface CodeFileReadResult {
  text: string;
  fileName: string;
  mimeType: string;
  language: string;
  truncated: boolean;
}

function extensionOf(name: string): string {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
}

function inferLanguage(ext: string, fileName: string): string {
  const map: Record<string, string> = {
    js: "javascript",
    jsx: "javascript",
    ts: "typescript",
    tsx: "typescript",
    py: "python",
    java: "java",
    go: "go",
    json: "json",
    yaml: "yaml",
    yml: "yaml",
    md: "markdown",
    txt: "text",
    text: "text",
    sh: "shell",
    bash: "shell",
    cs: "csharp",
    rb: "ruby",
    php: "php",
  };
  return map[ext] ?? (ext || fileName);
}

export async function readCodeFile(file: File): Promise<CodeFileReadResult> {
  if (file.size > MAX_CODE_FILE_BYTES) {
    throw new Error(`File exceeds ${MAX_CODE_FILE_BYTES / (1024 * 1024)} MB limit`);
  }

  const ext = extensionOf(file.name);
  if (!SUPPORTED_EXTENSIONS.has(ext)) {
    throw new Error(
      "Unsupported file type. Use .js, .ts, .py, .java, .go, .json, .yaml, .txt, or .md",
    );
  }

  let text = await file.text();
  let truncated = false;
  if (text.length > MAX_CODE_CHARS) {
    text = text.slice(0, MAX_CODE_CHARS);
    truncated = true;
  }

  return {
    text,
    fileName: file.name,
    mimeType: file.type || "application/octet-stream",
    language: inferLanguage(ext, file.name),
    truncated,
  };
}

export interface CodeReviewImportPayload {
  fileName: string;
  language: string;
  text: string;
  productUuid?: string | null;
}

export function buildCodeReviewMessage(payload: CodeReviewImportPayload): string {
  const body = JSON.stringify(payload);
  return `Review my integration code against the linked product APIs and developer guides.

\`\`\`integration-code-import
${body}
\`\`\``;
}

export function buildProductFocusMessage(
  productUuid: string,
  productName: string,
): string {
  return `I want to integrate with product "${productName}" (uuid: ${productUuid}). Load my subscriptions, this product's developer guides and APIs, and tell me where to start. Include an \`\`\`integration-product-context\`\`\` block.`;
}

export function buildCertificationMessage(
  productUuid: string,
  productName: string,
): string {
  return `Run a certification readiness assessment for product "${productName}" (uuid: ${productUuid}). Load developer guides and OpenAPI specs, then output an \`\`\`integration-certification-checklist\`\`\` block.`;
}
